<template>
  <div class="drs">
    <!-- every tour shares one value: there is nothing to slide between -->
    <div v-if="max <= min" class="drs__labels" style="justify-content: center;">
        <span>All tours: {{ formatValue(min) }}</span>
    </div>
    <template v-else>
      <div class="drs__labels" :class="{ 'is-single': isSingleValue }">
          <span v-if="isSingleValue" class="drs__single">{{ formatValue(modelMin) }}</span>
          <template v-else>
              <span>{{ formatValue(modelMin) }}</span>
              <span>{{ formatValue(modelMax) }}</span>
          </template>
      </div>
      <!-- one pointer handler for the whole track: it picks the nearest thumb, and when both thumbs sit on the same
           value the drag direction decides which one moves (left = minimum, right = maximum) -->
      <div
        ref="track"
        class="drs__track-wrap"
        :class="{ 'is-dragging': dragging }"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerUp"
      >
        <div class="drs__fill" :style="fillStyle"></div>
        <span
          v-for="tick in ticks"
          :key="tick"
          class="drs__tick"
          :class="{ 'is-in-range': tick >= modelMin && tick <= modelMax }"
          :style="{ left: percent(tick) + '%' }"
        ></span>
        <button
          ref="minThumb"
          type="button"
          class="drs__thumb"
          :class="{ 'is-active': active === 'min', 'is-merged': isSingleValue }"
          role="slider"
          :aria-label="'Minimum ' + formatValue(modelMin)"
          :aria-valuemin="min" :aria-valuemax="modelMax" :aria-valuenow="modelMin"
          :aria-valuetext="formatValue(modelMin)"
          :style="{ left: percent(modelMin) + '%', zIndex: active === 'min' ? 3 : 2 }"
          @keydown="onKeyDown('min', $event)"
        ></button>
        <button
          ref="maxThumb"
          type="button"
          class="drs__thumb"
          :class="{ 'is-active': active === 'max', 'is-merged': isSingleValue }"
          role="slider"
          :aria-label="'Maximum ' + formatValue(modelMax)"
          :aria-valuemin="modelMin" :aria-valuemax="max" :aria-valuenow="modelMax"
          :aria-valuetext="formatValue(modelMax)"
          :style="{ left: percent(modelMax) + '%', zIndex: active === 'max' ? 3 : 2 }"
          @keydown="onKeyDown('max', $event)"
        ></button>
      </div>
      <p v-if="isSingleValue" class="drs__hint">Drag left or right to widen the range</p>
    </template>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';

type Thumb = 'min' | 'max';

// pixels a press on two merged thumbs must move before the direction picks the thumb
const DIRECTION_THRESHOLD = 3;
// only draw tick marks when there are few enough values to tell apart
const MAX_TICKS = 24;

export default defineComponent({
  name: 'DualRangeSlider',
  props: {
    min:            { type: Number, required: true },
    max:            { type: Number, required: true },
    modelMin:       { type: Number, required: true },
    modelMax:       { type: Number, required: true },
    step:           { type: Number, default: 1 },
    // the values the tours actually have: the thumbs snap to these instead of every step in between
    values:         { type: Array as PropType<number[]>, default: () => [] },
    prefix:         { type: String, default: '' },
    suffix:         { type: String, default: '' },
    // used instead of suffix when the value is exactly 1 (e.g. " day" vs " days")
    singularSuffix: { type: String, default: '' },
  },
  emits: ['update:modelMin', 'update:modelMax'],
  data() {
    return {
      active: null as Thumb | null,
      dragging: false,
      // pressed on merged thumbs: wait for the first movement to know which way to go
      pendingDirection: false,
      startX: 0,
    };
  },
  computed: {
    isSingleValue(): boolean {
      return this.modelMin === this.modelMax;
    },
    // sorted, unique snap points within the bounds (always including both ends); empty = snap by step
    snapValues(): number[] {
      if (!this.values.length) return [];
      const inside = this.values.filter(v => v >= this.min && v <= this.max);
      return Array.from(new Set([this.min, ...inside, this.max])).sort((a, b) => a - b);
    },
    ticks(): number[] {
      return this.snapValues.length > 2 && this.snapValues.length <= MAX_TICKS ? this.snapValues : [];
    },
    fillStyle(): object {
      return { left: `${this.percent(this.modelMin)}%`, right: `${100 - this.percent(this.modelMax)}%` };
    },
  },
  methods: {
    formatValue(value: number): string {
      const suffix = value === 1 && this.singularSuffix ? this.singularSuffix : this.suffix;
      return `${this.prefix}${value}${suffix}`;
    },
    percent(value: number): number {
      const range = this.max - this.min;
      if (range <= 0) return 0;
      return Math.min(100, Math.max(0, ((value - this.min) / range) * 100));
    },
    snap(raw: number): number {
      if (this.snapValues.length) {
        return this.snapValues.reduce((best, v) => (Math.abs(v - raw) < Math.abs(best - raw) ? v : best), this.snapValues[0]);
      }
      const stepped = this.min + Math.round((raw - this.min) / this.step) * this.step;
      return Math.min(this.max, Math.max(this.min, stepped));
    },
    // the next snap point up (direction 1) or down (-1) from a value
    neighbour(value: number, direction: number): number {
      if (this.snapValues.length) {
        const list = direction > 0 ? this.snapValues : [...this.snapValues].reverse();
        return list.find(v => (direction > 0 ? v > value : v < value)) ?? value;
      }
      return Math.min(this.max, Math.max(this.min, value + direction * this.step));
    },
    valueAt(clientX: number): number {
      const rect = (this.$refs.track as HTMLElement).getBoundingClientRect();
      const ratio = rect.width > 0 ? Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)) : 0;
      return this.snap(this.min + ratio * (this.max - this.min));
    },
    setValue(thumb: Thumb, value: number) {
      if (thumb === 'min') {
        const next = Math.min(value, this.modelMax);
        if (next !== this.modelMin) this.$emit('update:modelMin', next);
      } else {
        const next = Math.max(value, this.modelMin);
        if (next !== this.modelMax) this.$emit('update:modelMax', next);
      }
    },
    focusThumb(thumb: Thumb) {
      (this.$refs[thumb === 'min' ? 'minThumb' : 'maxThumb'] as HTMLElement | undefined)?.focus({ preventScroll: true });
    },
    onPointerDown(e: PointerEvent) {
      if (e.button !== 0) return;
      e.preventDefault();
      (this.$refs.track as HTMLElement).setPointerCapture(e.pointerId);
      this.dragging = true;
      const value = this.valueAt(e.clientX);

      if (this.isSingleValue) {
        // pressing on (or right next to) the merged thumbs: the drag direction decides which thumb moves
        if (value === this.modelMin) {
          this.active = null;
          this.pendingDirection = true;
          this.startX = e.clientX;
          return;
        }
        this.active = value < this.modelMin ? 'min' : 'max';
      } else {
        const toMin = Math.abs(value - this.modelMin);
        const toMax = Math.abs(value - this.modelMax);
        this.active = toMin < toMax || (toMin === toMax && value < this.modelMin) ? 'min' : 'max';
      }
      this.setValue(this.active, value);
      this.focusThumb(this.active);
    },
    onPointerMove(e: PointerEvent) {
      if (!this.dragging) return;
      if (this.pendingDirection) {
        const dx = e.clientX - this.startX;
        if (Math.abs(dx) < DIRECTION_THRESHOLD) return;
        this.pendingDirection = false;
        this.active = dx < 0 ? 'min' : 'max';
        this.focusThumb(this.active);
      }
      if (this.active) this.setValue(this.active, this.valueAt(e.clientX));
    },
    onPointerUp(e: PointerEvent) {
      const track = this.$refs.track as HTMLElement | undefined;
      if (track?.hasPointerCapture(e.pointerId)) track.releasePointerCapture(e.pointerId);
      this.dragging = false;
      this.pendingDirection = false;
      this.active = null;
    },
    onKeyDown(thumb: Thumb, e: KeyboardEvent) {
      const current = thumb === 'min' ? this.modelMin : this.modelMax;
      let next: number;
      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowDown':  next = this.neighbour(current, -1); break;
        case 'ArrowRight':
        case 'ArrowUp':    next = this.neighbour(current, 1); break;
        case 'Home':       next = thumb === 'min' ? this.min : this.modelMin; break;
        case 'End':        next = thumb === 'min' ? this.modelMax : this.max; break;
        default: return;
      }
      e.preventDefault();
      this.setValue(thumb, next);
    },
  },
});
</script>
